import { NetworkStatus, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonAppearance,
  Icon,
  IconSize,
  MultiSelectCheckbox,
  Select,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  GET_PERMISSIONS,
  GET_ROLE_PERMISSIONS,
  SEARCH_ROLE,
  useAppUser,
  useDebounce,
} from '@bechtel/ht-clients-common';
import { Permission, Role, RolePermissionScope } from '@bechtel/ht-types';
import _, { filter, find, findIndex, get } from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ErrorBoundary, ErrorRetry } from '~/components';
import { ConfirmDialog } from '~/components/confirm-dialog';
import { SearchField } from '~/components/search-field';
import { useRolePermissionScope } from '~/hooks/use-role-permission-scope';
import { EmptySearchResultIcon } from '~/images';
import { PermissionMatrix } from './matrix';
import { Module, ModuleCollapsed } from './matrix/types';
import {
  ExpandAllStyled,
  FooterButtonStyled,
  FreezeCellStyled,
  JumpToListStyled,
  PermissionContainerStyled,
  PermissionFilterStyled,
  PermissionFooterStyled,
} from './styled';
const Item = ({ item }: any) => {
  return (
    <Text size="400" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
      {item.description}
    </Text>
  );
};

export const SelectedItem = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        Viewing {selectedItemCount} of {totalItemCount} Roles
      </Text>
    </Box>
  );
};
const ModuleItem = ({ item }: { item: Module }) => {
  return (
    <Text color="grey.500" size="400">
      {item.moduleName}
    </Text>
  );
};
export const ManagePermission = () => {
  const { appUser } = useAppUser();
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [showRoleError, setShowRoleError] = useState<boolean>(false);
  const [collapseAll, setCollapseAll] = useState<boolean>(false);
  const [collapseModules, setCollapseModules] = useState<ModuleCollapsed[]>([]);
  const [rolePermissions, setRolePermission] = useState<RolePermissionScope[]>([]);
  const defaultValues = useRef<RolePermissionScope[]>([]);
  const [newPermissions, setNewPermissions] = useState<RolePermissionScope[]>([]);
  const [deletedPermissions, setDeletedPermissions] = useState<RolePermissionScope[]>([]);
  const dispatchToast = useToastsDispatch();
  const { open: openWarningDialog, close: closeWarningDialog, visible: visibleWarningDialog } = useModalState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);

  const {
    data,
    loading: roleLoading,
    networkStatus: roleNetworkStatus,
    refetch: roleRefetch,
    error: roleError,
  } = useQuery<{ role: Role }>(SEARCH_ROLE, {
    onCompleted(data) {
      const roleListData: Role[] = get(data, 'searchRoles.items', []);
      const initialSelectedRoles: Role[] =
        roleListData.filter((role) => {
          return (
            role.name.toUpperCase() === 'FOREMAN' ||
            role.name.toUpperCase() === 'TOOL_ROOM_ATTENDANT' ||
            role.name.toUpperCase() === 'TOOL_ROOM_MANAGER' ||
            role.name.toUpperCase() === 'ADMIN' ||
            role.name.toUpperCase() === 'TOOL_ROOM_FOREMAN'
          );
        }) || [];

      filter(roleListData, (item, index) => {
        if (index <= 4) return item;
      });
      setSelectedRoles(initialSelectedRoles || []);
    },
    fetchPolicy: 'network-only',
  });
  const roleList = get(data, 'searchRoles.items', []);
  const { data: permissionListData } = useQuery<{ permission: Permission }>(GET_PERMISSIONS, {
    fetchPolicy: 'network-only',
  });
  const {
    loading: rolePermissionLoading,
    data: rolePermissionData,
    error: rolePermissionError,
    networkStatus: rolePermissionNetworkStatus,
  } = useQuery<{
    modules: RolePermissionScope[];
  }>(GET_ROLE_PERMISSIONS, {
    onCompleted(data) {
      const rolePermissionListData: RolePermissionScope[] = get(data, 'searchRolePermissionScopes.items', []);
      defaultValues.current = rolePermissionListData;
      setRolePermission([...rolePermissionListData]);
    },
    notifyOnNetworkStatusChange: true,
  });
  const permissionList: Permission[] = get(permissionListData, 'searchPermissions.items', []);
  const uniqueModuleNames = _.uniq(_.map(permissionList, 'module')).sort();
  const { addDeleteRolePermission, loading: updatingRolePermissions } = useRolePermissionScope();
  const addDeletedRoleMissionScope = () => {
    addDeleteRolePermission(
      {
        newRolePermissions: newPermissions?.map((rolePermission) => {
          return {
            permissionId: rolePermission.permissionId,
            roleId: rolePermission.roleId,
            scopeId: 10,
          };
        }),
        toBeDeleteRolePermissionIds: deletedPermissions.map((rolePermission) => {
          return rolePermission.id;
        }),
      },
      (data) => {
        const rolePermissionToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: data.errors ? ToastType.DANGER : ToastType.SUCCESS,
            content: data.errors ? `Permissions not updated successfully!` : `Permissions updated successfully`,
            autoDismissTimeout: 3000,
          },
        };
        dispatchToast(rolePermissionToast);
        setDeletedPermissions([]);
        setNewPermissions([]);
      }
    );
  };
  let moduleList: Module[] = uniqueModuleNames?.map((module: string, index) => {
    return {
      moduleId: index.toString(),
      moduleName: module,
      permissions: permissionList?.filter((x) => x?.module == module),
    };
  });
  const searchItemsItems = (item) => {
    const items =
      (moduleList && item.moduleName.toLowerCase().includes(debouncedSearchText.toLowerCase())) ||
      item.permissions.filter((x) => x.name.toLowerCase().includes(debouncedSearchText.toLowerCase().trim()))?.length >
        0 ||
      item.permissions.filter((x) => x.description.toLowerCase().includes(debouncedSearchText.toLowerCase().trim()))
        ?.length > 0 ||
      null;

    return items;
  };
  if (searchText) {
    moduleList = moduleList?.filter(searchItemsItems) ?? null;
  } else {
    moduleList = moduleList ?? null;
  }
  useEffect(() => {
    if (moduleList.length === 0) return;
    const collapsedList: ModuleCollapsed[] = moduleList.map(({ moduleId }) => ({
      moduleId: moduleId,
      collapsed: false,
    }));
    setCollapseModules(collapsedList);
  }, []);

  const onPermissionSelect = (roleId: string, permissionId: string) => {
    const index = findIndex(rolePermissions, (item) => {
      return item.roleId === roleId && item.permissionId === permissionId;
    });
    if (index >= 0) {
      rolePermissions.splice(index, 1);
      setRolePermission([...rolePermissions]);
    } else {
      rolePermissions.push({
        id: uuidv4(),
        permissionId: permissionId,
        roleId: roleId,
        scopeId: 10,
        projectId: appUser?.defaultProjectId || '',
      });
      setRolePermission([...rolePermissions]);
    }
    manageInsertOrDeleteList(roleId, permissionId);
  };

  const manageInsertOrDeleteList = (roleId: string, permissionId: string) => {
    const existingItem = find(defaultValues.current, (item) => {
      return item.roleId === roleId && item.permissionId === permissionId;
    });
    if (existingItem) {
      const index = findIndex(deletedPermissions, (item) => {
        return item.roleId === roleId && item.permissionId === permissionId;
      });
      if (index >= 0) {
        deletedPermissions.splice(index, 1);
        setDeletedPermissions([...deletedPermissions]);
      } else {
        deletedPermissions.push({
          id: existingItem.id,
          permissionId: existingItem.permissionId,
          roleId: existingItem.roleId,
          scopeId: 10,
          projectId: appUser?.defaultProjectId || '',
        });
        setDeletedPermissions([...deletedPermissions]);
      }
    } else {
      const index = findIndex(newPermissions, (item) => {
        return item.roleId === roleId && item.permissionId === permissionId;
      });
      if (index >= 0) {
        newPermissions.splice(index, 1);
        setNewPermissions([...newPermissions]);
      } else {
        newPermissions.push({
          id: uuidv4(),
          permissionId: permissionId,
          roleId: roleId,
          scopeId: 10,
          projectId: appUser?.defaultProjectId || '',
        });
        setDeletedPermissions([...deletedPermissions]);
      }
    }
  };
  const onExpandCollapseModule = (moduleId: string) => {
    setCollapseModules((prevState) => {
      const collapsedList = [...prevState];
      const selected = findIndex(collapsedList, (item) => {
        return item.moduleId === moduleId;
      });
      if (selected !== -1) {
        collapsedList[selected] = {
          ...collapsedList[selected],
          collapsed: !collapsedList[selected].collapsed,
        };
      }
      return collapsedList;
    });
  };
  const onAllExpandCollapse = () => {
    setCollapseAll(!collapseAll);
    setCollapseModules((prevState) => {
      const collapsedModules = [...prevState];
      const collapsedList = collapsedModules.map(({ moduleId }) => ({
        moduleId: moduleId,
        collapsed: !collapseAll,
      }));
      return collapsedList;
    });
  };
  const onDiscardChanges = () => {
    setRolePermission([...defaultValues.current]);
    setDeletedPermissions([]);
    setNewPermissions([]);
    closeWarningDialog();
  };
  const dirty = () => newPermissions.length > 0 || deletedPermissions.length > 0;
  const listRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const jumpToItem = (moduleName: string) => {
    const moduleItem = find(moduleList, (item) => {
      return item.moduleName === moduleName;
    });
    if (!moduleItem) return;
    listRef?.current?.scrollTo({
      top: itemsRef.current[moduleItem.moduleId].offsetTop - (itemsRef.current[0].parentElement?.offsetTop || 0),
      behavior: 'smooth',
    });
  };

  return (
    <ErrorBoundary>
      <PermissionContainerStyled>
        <PermissionFilterStyled>
          <Box style={{ width: '360px' }}>
            <SearchField
              placeholder="Search Module, Permission"
              size={TextFieldSize.MEDIUM}
              iconSize={IconSize.MEDIUM}
              onChange={(event) => {
                setSearchText(event.target.value);
              }}
            ></SearchField>
          </Box>

          <Box>
            {(roleLoading || rolePermissionLoading || roleNetworkStatus === NetworkStatus.refetch) && (
              <Box mr={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                <Spinner size={SpinnerSize.MEDIUM} />
              </Box>
            )}
            {!roleLoading && roleError && (
              <ErrorRetry refetch={roleRefetch}>
                There was a problem loading role list. Please click the button below to try again.
              </ErrorRetry>
            )}
            {!roleError && (
              <Box style={{ width: '208px' }} flexDirection={{ sm: 'column' }} mr={{ sm: '400' }}>
                <MultiSelectCheckbox
                  items={roleList}
                  itemComponent={Item}
                  selectedComponent={SelectedItem}
                  initialSelectedItems={[...selectedRoles]}
                  size={TextFieldSize.MEDIUM}
                  showSelectAll={true}
                  confirmSelection={true}
                  appearance={TextFieldAppearance.NORMAL}
                  displayKeyField="name"
                  loading={roleLoading || roleNetworkStatus === NetworkStatus.refetch}
                  onSelectedItemsChange={async (selectedItems) => {
                    if (!selectedItems || selectedItems.length === 0) setShowRoleError(true);
                    else setShowRoleError(false);
                    selectedItems && setSelectedRoles(selectedItems);
                  }}
                ></MultiSelectCheckbox>
                {showRoleError && (
                  <Box mt={{ sm: '300' }}>
                    <Text color="bechtel.red" size="400">
                      Please select one or more roles.
                    </Text>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </PermissionFilterStyled>

        {selectedRoles.length > 0 && moduleList.length > 0 && (
          <Fragment>
            <PermissionMatrix
              lstItemRefrence={itemsRef}
              lstRefrence={listRef}
              moduleList={moduleList}
              onPermissionSelect={onPermissionSelect}
              rolePermissionScopeList={rolePermissions}
              selectedRoles={selectedRoles}
              collapseModules={collapseModules}
              onExpandCollapseModule={onExpandCollapseModule}
            />
            <FreezeCellStyled>
              <Box style={{ width: '60%' }} alignItems={{ sm: 'center' }}>
                <Text size="300" color="grey.600">
                  Jump To :
                </Text>
                <JumpToListStyled>
                  <Select
                    name="moduleList"
                    placeholder=""
                    appearance={TextFieldAppearance.TRANSPARENT}
                    items={moduleList}
                    itemComponent={ModuleItem}
                    selectedComponent={ModuleItem}
                    initialSelectedItem={moduleList[0]}
                    onSelectedItemChange={({ selectedItem }) => {
                      jumpToItem(selectedItem.moduleName);
                    }}
                  />
                </JumpToListStyled>
              </Box>
              <ExpandAllStyled
                onClick={() => {
                  onAllExpandCollapse();
                }}
              >
                <Icon
                  size={IconSize.XSMALL}
                  icon={collapseAll ? 'up-right-and-down-left-from-center' : 'down-left-and-up-right-to-center'}
                  iconStyle="regular"
                  fillColor="primary.700"
                ></Icon>
                <Box justifyContent={{ sm: 'flex-start' }}>
                  <Text size="300" color="black">
                    {collapseAll ? 'Expand All' : 'Collapse All'}
                  </Text>
                </Box>
              </ExpandAllStyled>
            </FreezeCellStyled>
          </Fragment>
        )}

        {selectedRoles.length > 0 && moduleList.length > 0 && (
          <PermissionFooterStyled>
            <Box>
              <Box mr={{ sm: '400' }}>
                <Button
                  disabled={!dirty()}
                  appearance={ButtonAppearance.SECONDARY}
                  onClick={() => {
                    openWarningDialog();
                  }}
                >
                  Discard Changes
                </Button>
              </Box>
              <FooterButtonStyled
                disabled={!dirty()}
                appearance={ButtonAppearance.PRIMARY}
                onClick={() => {
                  addDeletedRoleMissionScope();
                }}
              >
                Save Changes
                {updatingRolePermissions && <Spinner />}
              </FooterButtonStyled>
            </Box>
          </PermissionFooterStyled>
        )}
        {moduleList.length <= 0 && (
          <Box
            mt={{ sm: '800' }}
            justifyContent={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            alignItems={{ sm: 'center' }}
          >
            <EmptySearchResultIcon></EmptySearchResultIcon>
            <Text color="grey.400">No Data found.</Text>
          </Box>
        )}
      </PermissionContainerStyled>
      {visibleWarningDialog && (
        <ConfirmDialog
          message={'Are you sure you want to discard the current changes?'}
          onCancel={closeWarningDialog}
          title="Discard Changes"
          onConfirm={onDiscardChanges}
          cancelText="CANCEL"
          confirmText="YES, DISCARD"
        ></ConfirmDialog>
      )}
    </ErrorBoundary>
  );
};
